import { getProductData, type ProductData } from "~/domains/product/types/ProductData";
import { IMG_SUCCESSOR_BOX } from "~/utils/url/staticResources";
import { Trans } from "react-i18next";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { Box, Stack, styled } from "~/common/mui";

const NotificationRoot = styled("a", {
  shouldForwardProp: (prop) => prop !== "$variant",
})<{ $variant?: "basic" | "alternative" }>(({ theme, $variant }) => ({
  display: "flex",
  flexFlow: "column",
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `3px 0 0 0 ${$variant === "alternative" ? theme.palette.info.main : theme.palette.success.main} inset`,
  borderRadius: theme.spacing(1),
  background: theme.palette.surfaceContainer.lowest,
  color: theme.palette.text.primary,
}));

const ContentRow = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  gap: theme.spacing(2),
}));

const ImageSection = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  flex: "none",
  height: theme.spacing(8),
  width: theme.spacing(8),
}));

const BoxImage = styled("img")(({ theme }) => ({
  width: theme.spacing(12.5),
  height: theme.spacing(12.5),
  objectFit: "cover",
  objectPosition: "center",
}));

const Shoutout = styled("p")(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: 0,
  marginBottom: theme.spacing(2.5),
}));

const HeadlineBig = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  fontWeight: "bold",
  marginBottom: theme.spacing(0.5),
}));

const Description = styled("p")(({ theme }) => ({
  color: theme.palette.text.secondary,
  ...theme.typography.bodyMedium,
  margin: 0,
}));

const PseudoLink = styled("span")(({ theme }) => ({
  ...theme.typography.bodyMedium,
  fontWeight: "bold",
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
}));

const Arrow = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1.5),
}));

interface Props {
  variant?: "basic" | "extended";
  className?: string;
  successor: ProductData | ProductDescriptor;
  discontinued?: ProductData | ProductDescriptor;
}

export const SuccessorNotification = (props: Props) => {
  const successor = getProductData(props.successor);
  const { variant = "basic" } = props;

  return (
    <NotificationRoot
      className={props.className}
      $variant="basic"
      tracking-aux-aspr-type={"New"}
      tracking-aux-aspr-name={successor.designation}
      tracking-aux-aspr-id={successor.code}
      href={successor.url}
    >
      <ContentRow direction="row">
        <ImageSection>
          <BoxImage src={IMG_SUCCESSOR_BOX} alt={""} />
        </ImageSection>
        <Box>
          <HeadlineBig>
            <Trans i18nKey={"page.pdp.phaseinout.successor.headline"}></Trans>
          </HeadlineBig>

          {variant === "extended" && (
            <Shoutout>
              <Trans i18nKey={"page.pdp.phaseinout.successor.shoutout"}></Trans>
            </Shoutout>
          )}

          <Description>
            <Trans i18nKey={"page.pdp.phaseinout.successor.description"}>
              We invite you to switch to the newer version of this product:{" "}
              <PseudoLink>{{ designation: successor.designation }}</PseudoLink>
            </Trans>
          </Description>
        </Box>
        <Arrow>
          <ArrowForwardOutlinedIcon />
        </Arrow>
      </ContentRow>
    </NotificationRoot>
  );
};
